// Modules
import React from 'react';
import { Cell, CellProps } from 'react-table';

// Types
import { IBasstrakkAngler } from '../../types';

// Component
import NoActivity from '../../components/Column/NoActivity';
import ActivityCatchCell from './ActivityCatchCell';

// Util
import { formatTime } from '../../util/util';

// Config
import AnglerCell from '../../components/Column/AnglerCell';
import { CLASS_PREFIX, NO_CATCHES_TEXT } from '../../config';
/**
 * Basstrakk Standings columns
 */
export function getBasstrakkColumns(tournamentSeries: string, view: string) {
  const columns = [
    {
      Header: 'Rank',
      accessor: 'Rank',
      id: 'rank',
      className: `${CLASS_PREFIX}cell ${CLASS_PREFIX}cell--700 ${CLASS_PREFIX}cell__rank`,
      headerClassName: `${CLASS_PREFIX}header__rank`,
      width: 35,
      align: 'center',
    },
    {
      Header: 'Angler', // ▶
      id: 'angler', // Must have an id
      accessor: 'FISHERMAN_PHONENAME',
      className: `${CLASS_PREFIX}cell ${CLASS_PREFIX}cell--700 ${CLASS_PREFIX}cell__angler`,
      headerClassName: `${CLASS_PREFIX}header__angler`,
      width: 125,
      minWidth: 115,
      maxWidth: 200,
      Cell: ({ row, index, width }: CellProps<IBasstrakkAngler>) => (
        <AnglerCell
          anglerName={row.values.angler}
          isLeader={
            row.original.FISHERMENID === row.original.BigBassLeader.AnglerId
          }
          index={index}
          isExpanded={row.isExpanded}
          width={width}
          tid={row.original.TournamentId}
        />
      ),
    },
    {
      Header: 'Activity',
      // We want this column to still be sortable so we will use the weight
      // (string) as the accessor value
      accessor: originalRow => {
        const lastCatch = originalRow.Catches.slice(-1)[0];
        // If an angler doesn't have any catches just say so
        if (!lastCatch) {
          return 'No Activity';
        }
        // By returning the weight as the value for the accessor this allows
        // this column to be sorted by that weight and not by what's returned in
        // the "Cell" below. There would be no need to sort
        // since all the columns would start with (New Catch...)
        return lastCatch.FishWeightPdsOz;
      },
      className: `${CLASS_PREFIX}cell ${CLASS_PREFIX}cell__activity`,
      headerClassName: `${CLASS_PREFIX}header__activity`,
      minWidth: 175,
      Cell: ({ value, row }) => {
        if (value !== NO_CATCHES_TEXT) {
          const lastCatch = row.original.Catches.slice(-1)[0];
          return (
            <ActivityCatchCell
              key={row.original.FISHERMAN_PHONENAME}
              row={row}
              lastCatch={lastCatch}
            />
          );
        }
        return <NoActivity classPrefix={CLASS_PREFIX} />;
      },
    },
    // Total
    {
      Header: 'Total',
      className: `${CLASS_PREFIX}cell`,
      headerClassName: `${CLASS_PREFIX}header`,
      columns: [
        {
          Header: 'Count',
          accessor: 'SumFishCount',
          className: ` ${CLASS_PREFIX}cell ${CLASS_PREFIX}cell--br-none ${CLASS_PREFIX}cell--count`,
          headerClassName: `${CLASS_PREFIX}header__count ${CLASS_PREFIX}cell--br-none ${CLASS_PREFIX}header--count`,
          width: 45,
          // minWidth: 100,
          // maxWidth: 200,
        },
        {
          Header: 'Weight',
          accessor: 'SumPdsOz',
          className: `${CLASS_PREFIX}cell ${CLASS_PREFIX}cell--700 ${CLASS_PREFIX}cell--br-none`,
          headerClassName: `${CLASS_PREFIX}header ${CLASS_PREFIX}cell--br-none`,
          width: 45,
        },
        {
          Header: 'Back',
          accessor: 'BackPdsOz',
          className: `${CLASS_PREFIX}cell`,
          headerClassName: `${CLASS_PREFIX}header`,
          // width: 25,
        },
      ],
    },
    // Livewell
    {
      Header: 'Livewell',
      className: `${CLASS_PREFIX}cell`,
      headerClassName: `${CLASS_PREFIX}header`,
      columns: [
        {
          Header: 'Count',
          accessor: 'BTFishLivewell',
          className: `${CLASS_PREFIX}cell ${CLASS_PREFIX}cell--br-none`,
          headerClassName: `${CLASS_PREFIX}header ${CLASS_PREFIX}cell--br-none`,
        },
        {
          Header: 'Weight',
          accessor: 'BTLimitPdsOz',
          className: `${CLASS_PREFIX}cell ${CLASS_PREFIX}cell--700`,
          headerClassName: `${CLASS_PREFIX}header`,
          // minWidth: 75,
        },
      ],
    },
    // Today
    {
      Header: 'Today',
      className: `${CLASS_PREFIX}cell`,
      headerClassName: `${CLASS_PREFIX}header`,
      columns: [
        {
          Header: 'Count',
          accessor: 'BTFishCount',
          className: `${CLASS_PREFIX}cell ${CLASS_PREFIX}cell--br-none`,
          headerClassName: `${CLASS_PREFIX}header ${CLASS_PREFIX}cell--br-none`,
        },
        {
          Header: 'Weight',
          accessor: 'BTKeptPdsOz',
          className: `${CLASS_PREFIX}cell ${CLASS_PREFIX}cell--700 ${CLASS_PREFIX}cell--br-none`,
          headerClassName: `${CLASS_PREFIX}header ${CLASS_PREFIX}cell--br-none`,
        },
        {
          Header: 'Updated',
          accessor: 'LastLocationTime',
          className: `${CLASS_PREFIX}cell`,
          headerClassName: `${CLASS_PREFIX}header`,
          Cell: ({ value }: Cell) => (value ? formatTime(value) : ''),
          minWidth: 75,
        },
      ],
    }
  ];

  if (view !== 'coanglers') {
    // AOY Rank
    // @todo render this column when we have AOY figured out
    columns.push({
      Header: ['elite', 'classic'].includes(tournamentSeries) ? 'AOY' : 'EQ',
      className: `${CLASS_PREFIX}cell`,
      headerClassName: `${CLASS_PREFIX}header`,
      columns: [
        {
          Header: 'Rank',
          id: 'aoyRank',
          accessor: originalRow => {
            return originalRow.AoyStanding && originalRow.AoyStanding.AOYRank
              ? originalRow.AoyStanding.AOYRank
              : 0;
          },
          className: `${CLASS_PREFIX}cell ${CLASS_PREFIX}cell--700`,
          headerClassName: `${CLASS_PREFIX}header`,
        },
      ],
    });
  }
  return columns;
}
